import { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { useAuth } from '@/lib/auth-context';
import { useProfile } from '@/lib/profile-context';
import { sellersAPI } from '@/lib/api';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Menu, RefreshCw, Download, CheckCircle, AlertCircle, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const SellerContext = createContext(null);

export function useSeller() {
  const ctx = useContext(SellerContext);
  if (!ctx) throw new Error('useSeller must be used within DashboardLayout');
  return ctx;
}

export default function DashboardLayout() {
  const { user, isAdmin } = useAuth();
  const { accountId, profileId, hasSelection, currentProfile } = useProfile();
  const [sellers, setSellers] = useState([]);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [currentSellerId, setCurrentSellerId] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [syncVersion, setSyncVersion] = useState(0);
  const dateFromRef = useRef('');
  const dateToRef = useRef('');
  const syncLockRef = useRef(false);
  const initialAutoSyncDoneRef = useRef(false);
  const autoSyncedSelectionsRef = useRef(new Set());

  const loadSellers = useCallback(async () => {
    if (!accountId || !profileId) {
      setSellers([]);
      setCurrentSellerId(null);
      setLastSynced(null);
      setLoadingSellers(false);
      return;
    }
    setLoadingSellers(true);
    try {
      const params = {};
      params.account_id = accountId;
      params.profile_id = profileId;
      const res = await sellersAPI.getAll(params);
      const data = Array.isArray(res.data) ? res.data : [];
      setSellers(data);
      if (data.length > 0) {
        setCurrentSellerId(data[0].id);
        if (data[0]?.last_synced) {
          setLastSynced(new Date(data[0].last_synced));
        } else {
          setLastSynced(null);
        }
      } else {
        setCurrentSellerId(null);
        setLastSynced(null);
      }
    } catch (err) {
      console.error('Failed to load sellers:', err);
    } finally {
      setLoadingSellers(false);
    }
  }, [accountId, profileId]);

  useEffect(() => {
    loadSellers();
  }, [loadSellers]);

  // Realtime polling: refresh sidebar scores even without a full sync.
  useEffect(() => {
    if (!hasSelection) return;
    const id = setInterval(() => {
      loadSellers();
    }, 45000);
    return () => clearInterval(id);
  }, [hasSelection, loadSellers]);

  const currentSeller = sellers.find(s => s.id === currentSellerId) || null;

  const handleSync = useCallback(async (overrideDateFrom, overrideDateTo, options = {}) => {
    const { silent = false, source = 'manual' } = options;
    if (syncLockRef.current) return;
    if (!accountId || !profileId) {
      toast.error('Please select an account and profile first');
      return;
    }
    syncLockRef.current = true;
    setSyncing(true);
    try {
      const payload = {
        account_id: accountId,
        profile_id: profileId,
      };
      const df = overrideDateFrom || dateFromRef.current;
      const dt = overrideDateTo || dateToRef.current;
      if (df) payload.date_from = df;
      if (dt) payload.date_to = dt;

      console.log('[Protego Sync] Sending payload:', payload);
      const res = await sellersAPI.syncByProfile(payload);
      const data = res.data || {};
      const syncedAt = data.last_synced || data.data?.last_synced;
      if (syncedAt) {
        setLastSynced(new Date(syncedAt));
      } else {
        setLastSynced(new Date());
      }
      const sid = data.seller_id ?? data.data?.seller_id;
      await loadSellers();
      if (sid) {
        setCurrentSellerId(sid);
      }
      setSyncVersion(v => v + 1);
      const rangeLabel = df || dt ? ` (${df || 'start'} → ${dt || 'now'})` : '';
      const score = data.protego_score ?? data.health_score ?? data.data?.finalScore;
      if (!silent) {
        toast.success(`Synced — Protego Score: ${score ?? 'N/A'}${rangeLabel}`);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Sync failed';
      if (silent && source === 'initial-load') {
        toast.error(`Auto sync failed on page load: ${msg}. You can retry with "Sync Now".`);
      } else {
        toast.error(msg);
      }
    } finally {
      syncLockRef.current = false;
      setSyncing(false);
    }
  }, [accountId, profileId, loadSellers]);

  // Auto-sync only once on initial page/app load when a selection already exists.
  // Do not auto-sync again on account/profile changes within the same session.
  useEffect(() => {
    if (initialAutoSyncDoneRef.current) return;
    initialAutoSyncDoneRef.current = true;

    if (!accountId || !profileId) return;
    handleSync(undefined, undefined, { silent: true, source: 'initial-load' });
  }, [accountId, profileId, handleSync]);

  // Auto-sync once per selected account/profile when no seller data exists yet.
  useEffect(() => {
    if (!accountId || !profileId) return;
    if (loadingSellers || syncing) return;
    if (sellers.length > 0) return;
    const selectionKey = `${accountId}:${profileId}`;
    if (autoSyncedSelectionsRef.current.has(selectionKey)) return;
    autoSyncedSelectionsRef.current.add(selectionKey);
    handleSync(undefined, undefined, { silent: true, source: 'selection-empty' });
  }, [accountId, profileId, loadingSellers, syncing, sellers.length, handleSync]);

  const handleDownloadReport = async () => {
    if (!currentSellerId) return;
    toast.success('Generating audit report...');
    try {
      const res = await sellersAPI.getAuditReport(currentSellerId);
      const report = res.data;
      const lines = [
        '═══════════════════════════════════════════════════════════════',
        '              PROTEGO AI — ENFORCEMENT AUDIT REPORT',
        '═══════════════════════════════════════════════════════════════',
        '',
        `Account:        ${report.account}`,
        `Marketplace:    ${report.marketplace}`,
        `Model:          ${report.model}`,
        `Protego Score:  ${report.protego_score ?? report.health_score}/100 (${report.risk_label})`,
        `Generated:      ${new Date(report.generated_at).toLocaleString()}`,
        '',
        '───────────────────────────────────────────────────────────────',
        '  EXECUTIVE SUMMARY',
        '───────────────────────────────────────────────────────────────',
        '',
        `Total Metrics Monitored:  ${report.total_metrics}`,
        `Metrics in Breach:        ${report.breached_count}`,
        `Metrics in Warning:       ${report.warning_count}`,
        `Metrics Safe:             ${report.safe_count}`,
        `Open Action Items:        ${report.open_tasks}`,
        `ASINs at Risk:            ${report.asins_at_risk}`,
        '',
        '───────────────────────────────────────────────────────────────',
        '  TOP RISK SIGNALS (Score Impact)',
        '───────────────────────────────────────────────────────────────',
        '',
        ...(report.top_signals || []).map((s, i) => `  ${i+1}. ${s.name}: ${s.value} (impact: -${s.contribution} pts)`),
        '',
        '───────────────────────────────────────────────────────────────',
        '  BREACHED METRICS — DETAIL',
        '───────────────────────────────────────────────────────────────',
        '',
        ...(report.breached_metrics || []).map(m => [
          `  * ${m.name}`,
          `    Current: ${m.value}  |  Threshold: ${m.threshold}  |  Penalty: -${m.penalty} pts`,
          `    Action: ${m.sop}`,
          '',
        ]).flat(),
        '───────────────────────────────────────────────────────────────',
        '  ACTION PLAN — NEXT 7 DAYS',
        '───────────────────────────────────────────────────────────────',
        '',
        ...(report.action_plan || []).map((a, i) => `  ${i+1}. [${a.priority}] ${a.metric}: ${a.action}`),
        '',
        '───────────────────────────────────────────────────────────────',
        '  RECENT ALERTS',
        '───────────────────────────────────────────────────────────────',
        '',
        ...(report.recent_alerts || []).map(a => `  [${a.severity}] ${a.metric} — triggered at ${a.value}`),
        '',
        '═══════════════════════════════════════════════════════════════',
        '  Report generated by Protego AI',
        '═══════════════════════════════════════════════════════════════',
      ];
      const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Protego-Audit-Report-${(report.account || 'Account').replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to generate report');
    }
  };

  const getSyncText = () => {
    if (!lastSynced) return 'Never synced';
    const diff = Math.floor((Date.now() - lastSynced.getTime()) / 1000);
    if (diff < 60) return 'Synced just now';
    if (diff < 3600) return `Synced ${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `Synced ${Math.floor(diff / 3600)}h ago`;
    return `Synced ${Math.floor(diff / 86400)}d ago`;
  };

  const neverSynced = lastSynced === null;
  const BACKFILL_WINDOW_MS = 4 * 60 * 60 * 1000; // up to 4 hours
  const profileCreatedAtMs = currentProfile?.created_at ? new Date(currentProfile.created_at).getTime() : null;
  const profileAgeMs = profileCreatedAtMs ? (Date.now() - profileCreatedAtMs) : null;
  const isBackfillWindow = hasSelection && !currentSellerId && profileAgeMs !== null && profileAgeMs >= 0 && profileAgeMs < BACKFILL_WINDOW_MS;
  const isExtendedBackfillWindow = isBackfillWindow && profileAgeMs >= (2 * 60 * 60 * 1000);
  const backfillRemainingMs = isBackfillWindow ? Math.max(0, BACKFILL_WINDOW_MS - profileAgeMs) : 0;
  const backfillRemainingMin = Math.ceil(backfillRemainingMs / 60000);

  return (
    <SellerContext.Provider value={{ sellers, currentSeller, currentSellerId, setCurrentSellerId, syncVersion, syncing, dateFrom, dateTo }}>
      <div className="min-h-screen flex bg-background">
        <aside className="hidden lg:block flex-shrink-0">
          <div className="sticky top-0 h-screen overflow-hidden">
            <Sidebar currentSeller={currentSeller} sellers={sellers} onSellerChange={setCurrentSellerId} />
          </div>
        </aside>

        <main className="flex-1 min-w-0">
          <header className="sticky top-0 z-30 border-b border-border/60 bg-background/80 backdrop-blur-sm supports-[backdrop-filter]:bg-background/60">
            <div className="flex items-center justify-between px-4 lg:px-8 h-14">
              <div className="flex items-center gap-3">
                <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="sm" className="lg:hidden h-9 w-9 p-0" data-testid="mobile-menu-button">
                      <Menu className="w-5 h-5" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="p-0 w-[264px]">
                    <Sidebar currentSeller={currentSeller} sellers={sellers} onSellerChange={setCurrentSellerId} onClose={() => setMobileOpen(false)} />
                  </SheetContent>
                </Sheet>
              </div>

              <div className="flex items-center gap-3">
                {hasSelection && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="sync-status">
                    {neverSynced ? (
                      <AlertCircle className="w-3.5 h-3.5 text-yellow-500" />
                    ) : (
                      <CheckCircle className="w-3.5 h-3.5 text-[hsl(142,72%,45%)]" />
                    )}
                    <span>{getSyncText()}</span>
                  </div>
                )}

                <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-8 px-3 text-xs gap-1.5 ${dateFrom || dateTo ? 'text-blue-500 hover:text-blue-600' : 'text-muted-foreground hover:text-foreground'}`}
                      disabled={!hasSelection}
                    >
                      <Calendar className="w-3.5 h-3.5" />
                      {dateFrom || dateTo ? `${dateFrom || '…'} → ${dateTo || '…'}` : 'Date Range'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-4" align="end">
                    <div className="space-y-3">
                      <p className="text-xs font-medium text-foreground">Sync with date range</p>
                      <p className="text-xs text-muted-foreground">
                        Set a date range to test KPI calculations against historical data. Leave empty for live/current data.
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-muted-foreground block mb-1">From</label>
                          <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => {
                              setDateFrom(e.target.value);
                              dateFromRef.current = e.target.value;
                            }}
                            className="w-full h-8 px-2 text-xs border border-border rounded-md bg-background text-foreground"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground block mb-1">To</label>
                          <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => {
                              setDateTo(e.target.value);
                              dateToRef.current = e.target.value;
                            }}
                            className="w-full h-8 px-2 text-xs border border-border rounded-md bg-background text-foreground"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          className="flex-1 h-7 text-xs"
                          disabled={syncing || !hasSelection}
                          onClick={() => {
                            const df = dateFromRef.current;
                            const dt = dateToRef.current;
                            setDatePickerOpen(false);
                            handleSync(df, dt);
                          }}
                        >
                          <RefreshCw className={`w-3 h-3 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                          Sync with Range
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => {
                            setDateFrom('');
                            setDateTo('');
                            dateFromRef.current = '';
                            dateToRef.current = '';
                          }}
                        >
                          Clear
                        </Button>
                      </div>
                      <div className="text-[10px] text-muted-foreground space-y-0.5">
                        <p>Quick ranges:</p>
                        <div className="flex flex-wrap gap-1">
                          {[
                            { label: 'Last 30d', from: new Date(Date.now() - 30*86400000), to: new Date() },
                            { label: 'Last 90d', from: new Date(Date.now() - 90*86400000), to: new Date() },
                            { label: 'Q4 2025', from: new Date('2025-10-01'), to: new Date('2025-12-31') },
                            { label: 'Q1 2026', from: new Date('2026-01-01'), to: new Date('2026-03-31') },
                            { label: 'Last 6 months', from: new Date(Date.now() - 180*86400000), to: new Date() },
                          ].map(({ label, from, to }) => (
                            <button
                              key={label}
                              className="px-1.5 py-0.5 rounded border border-border text-[10px] hover:bg-muted transition-colors"
                              onClick={() => {
                                const f = from.toISOString().split('T')[0];
                                const t = to.toISOString().split('T')[0];
                                setDateFrom(f);
                                setDateTo(t);
                                dateFromRef.current = f;
                                dateToRef.current = t;
                              }}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSync()}
                  disabled={syncing || !hasSelection}
                  data-testid="sync-now-button"
                  className="h-8 px-3 text-xs text-muted-foreground hover:text-foreground gap-1.5"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Syncing...' : 'Sync Now'}
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDownloadReport}
                  disabled={!hasSelection || !currentSellerId}
                  data-testid="download-audit-report-button"
                  className="h-8 px-3 text-xs text-muted-foreground hover:text-foreground gap-1.5"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download Audit Report
                </Button>
              </div>
            </div>
          </header>

          <div className="px-4 lg:px-8 py-6 bg-noise">
            {isBackfillWindow ? (
              <div className="rounded-lg border border-[hsl(188,100%,55%)]/25 bg-[hsl(188,100%,55%)]/8 p-6">
                <p className="text-sm font-medium text-foreground">Data backfill in progress</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {isExtendedBackfillWindow
                    ? 'This account/profile is still within the extended backfill window (2-4 hours). Data ingestion is in progress, so score and KPI trends may not be available yet.'
                    : 'This account/profile was connected recently. We are backfilling Amazon data before calculating score and KPI trends.'}
                  {' '}Estimated remaining time: up to {backfillRemainingMin} minute{backfillRemainingMin === 1 ? '' : 's'}.
                </p>
              </div>
            ) : hasSelection && !currentSellerId && (loadingSellers || syncing) ? (
              <div className="rounded-lg border border-border/60 bg-muted/20 p-6">
                <p className="text-sm font-medium text-foreground">Syncing seller data...</p>
                <p className="text-xs text-muted-foreground mt-1">
                  We are syncing this account/profile now and will load the dashboard automatically.
                </p>
              </div>
            ) : (
            <Outlet />
            )}
          </div>
        </main>
      </div>
    </SellerContext.Provider>
  );
}
